(() => {
  if (customElements.get('rb-pillow-reviews')) return;
  const cache = new Map();
  const starPath = 'M10 1.5 12.6 6.8 18.5 7.7 14.25 11.85 15.25 17.7 10 14.95 4.75 17.7 5.75 11.85 1.5 7.7 7.4 6.8Z';
  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  };
  const motion = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  function starGraphic() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 110 20');
    svg.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 5; i++) {
      const path = document.createElementNS(svg.namespaceURI, 'path');
      path.setAttribute('d', starPath);
      path.setAttribute('transform', `translate(${i * 22} 0)`);
      svg.append(path);
    }
    return svg;
  }
  function stars(rating, label) {
    const span = el('span', 'rbnr-stars');
    span.setAttribute('role', 'img');
    span.setAttribute('aria-label', `${rating} ${label}`);
    span.style.setProperty('--rbnr-fill', `${Math.max(0, Math.min(5, rating)) * 20}%`);
    const fill = el('span', 'rbnr-stars-fill');
    span.append(starGraphic());
    fill.append(starGraphic());
    span.append(fill);
    return span;
  }
  const reviewRows = (data, productId) => {
    if (data.version !== 1 || String(data.product_id) !== String(productId) || !Array.isArray(data.reviews)) throw new Error('Invalid review data');
    return data.reviews.filter(row => typeof row.id === 'string' && typeof row.body === 'string' && typeof row.name === 'string' && Number.isInteger(row.rating) && row.rating >= 1 && row.rating <= 5 && String(row.product_id) === String(productId) && row.published !== false && row.active !== false && row.status !== 'unpublished' && row.status !== 'hidden');
  };
  const reviewSummary = rows => ({count:rows.length,rating:rows.length ? Math.round(rows.reduce((sum,row) => sum + row.rating,0) / rows.length * 10) / 10 : 0});
  function reviewData(url) {
    if (!cache.has(url)) cache.set(url, fetch(url,{credentials:'omit'}).then(response => {
      if (!response.ok) throw new Error('Review data unavailable');
      return response.json();
    }).catch(error => {cache.delete(url);throw error;}));
    return cache.get(url);
  }
  class LiveReviewRating extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      reviewData(this.dataset.source).then(data => {
        const summary = reviewSummary(reviewRows(data,this.dataset.productId));
        this.replaceChildren();
        if (!summary.count) {this.hidden = true;return;}
        const formatter = new Intl.NumberFormat(this.dataset.language || 'en',{maximumFractionDigits:1});
        const label = `${formatter.format(summary.rating)} ${this.dataset.outOf}, ${formatter.format(summary.count)} ${this.dataset.reviews}`;
        this.append(stars(summary.rating,this.dataset.outOf),el('span','rb-live-review-count',`(${formatter.format(summary.count)})`));
        this.setAttribute('aria-label',label);
        this.hidden = false;
      }).catch(() => {this.hidden = true;});
    }
  }
  if (!customElements.get('rb-live-review-rating')) customElements.define('rb-live-review-rating',LiveReviewRating);
  class NativeReviews extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.events = new AbortController();
      const on = (node, name, listener, options = {}) => node?.addEventListener(name, listener, {...options, signal:this.events.signal});
      this.mode = this.dataset.mode;
      this.labels = JSON.parse(this.querySelector('[data-rbnr-labels]').textContent);
      const summaryRating = Number(this.dataset.rating), summaryCount = Number(this.dataset.count);
      this.querySelectorAll('[data-rating-value]').forEach(node => { node.textContent = this.number(summaryRating); });
      this.querySelectorAll('[data-review-count]').forEach(node => { node.textContent = this.number(summaryCount); });
      this.querySelectorAll('.rbnr-stars').forEach(node => node.setAttribute('aria-label', `${this.number(summaryRating)} ${this.text('out_of')}`));
      this.querySelector('.rbnr-rating')?.setAttribute('aria-label', `${this.text('rating_summary', {rating:summaryRating,count:summaryCount,review_label:this.text(summaryCount === 1 ? 'review' : 'reviews')})}. ${this.text('read_reviews')}`);
      this.rows = [];
      this.limit = Math.max(1, Math.min(50, Number(this.dataset.pageSize) || 6));
      this.shown = this.limit;
      this.slide = 0;
      this.list = this.querySelector('[data-rbnr-list]');
      this.status = this.querySelector('[data-rbnr-status]');
      this.dialog = this.querySelector('[data-rbnr-dialog]');
      on(this.querySelector('[data-rbnr-more]'), 'click', () => {
        const firstNew = this.shown;
        this.shown += this.limit;
        this.render();
        const card = this.list.children[firstNew];
        if (card) { card.tabIndex = -1; card.focus({preventScroll:true}); }
      });
      on(this.querySelector('[data-rbnr-gallery-toggle]'), 'click', event => {
        const gallery = this.querySelector('[data-rbnr-gallery]');
        if (!gallery) return;
        const expanded = gallery.dataset.expanded !== 'true';
        gallery.dataset.expanded = String(expanded);
        event.currentTarget.setAttribute('aria-expanded', String(expanded));
        event.currentTarget.textContent = expanded ? event.currentTarget.dataset.less : event.currentTarget.dataset.more;
      });
      on(this.querySelector('[data-rbnr-retry]'), 'click', () => this.load());
      this.querySelectorAll('select,input[type=checkbox]').forEach(node => on(node, 'change', () => {
        this.shown = this.limit;
        this.render();
      }));
      on(this, 'keydown', event => {
        if (this.mode !== 'snippets' || this.dialog?.open || !['ArrowLeft','ArrowRight'].includes(event.key) || /INPUT|SELECT|TEXTAREA/.test(event.target.tagName)) return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      });
      let touchStart;
      on(this.list, 'touchstart', event => { touchStart = {x:event.changedTouches[0].clientX,y:event.changedTouches[0].clientY}; }, {passive:true});
      on(this.list, 'touchend', event => {
        if (this.mode !== 'snippets' || !touchStart) return;
        const dx = event.changedTouches[0].clientX - touchStart.x, dy = event.changedTouches[0].clientY - touchStart.y;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.suppressClickUntil = Date.now() + 400;
          this.move(dx < 0 ? 1 : -1);
        }
        touchStart = null;
      }, {passive:true});
      this.querySelectorAll('[data-rbnr-prev]').forEach(button => on(button, 'click', () => this.move(-1)));
      this.querySelectorAll('[data-rbnr-next]').forEach(button => on(button, 'click', () => this.move(1)));
      on(this.querySelector('[data-rbnr-close]'), 'click', () => this.dialog.close());
      on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      });
      on(this.dialog, 'close', () => this.finishDialogSession());
      on(this.dialog, 'keydown', event => {
        if (event.key !== 'Tab') return;
        const targets = [...this.dialog.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]')].filter(node => node.getClientRects().length);
        const first = targets[0], last = targets[targets.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      });
      on(this.querySelector('.rbnr-rating'), 'click', event => {
        const target = document.getElementById(event.currentTarget.hash.slice(1));
        if (target) {
          event.preventDefault();
          target.scrollIntoView({behavior:motion()});
          target.focus({preventScroll:true});
        }
      });
      on(this.querySelector('[data-rbnr-current-link]'), 'click', async event => {
        event.preventDefault();
        const row = this.rows[this.slide];
        const feed = [...document.querySelectorAll('rb-pillow-reviews[data-mode="feed"]')].find(node => node.dataset.productId === this.dataset.productId);
        const success = row && feed && await feed.revealReview(row.id);
        this.status.textContent = success ? '' : this.text('selected_unavailable');
      });
      if (this.mode === 'rating' || this.mode === 'snippets') {
        if (!this.hasAttribute('data-rb-nearby') || !('IntersectionObserver' in window)) {
          this.load();
          return;
        }
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.load();
        }, {rootMargin: '400px'});
        this.observer.observe(this);
        on(this, 'focusin', () => this.load());
        return;
      }
      const openHash = () => {
        const prefix = `#RBReview-${this.dataset.productId}-`;
        if (location.hash.startsWith(prefix)) {
          try { this.revealReview(decodeURIComponent(location.hash.slice(prefix.length))); } catch { /* Invalid URL fragment. */ }
          return true;
        }
        return false;
      };
      on(window, 'hashchange', openHash);
      if (openHash()) return;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
        }, {rootMargin:'600px'});
        this.observer.observe(this);
      } else this.load();
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.finishDialogSession(false);
      this.observer?.disconnect();
      this.events?.abort();
      clearTimeout(this.highlightTimer);
      this.started = false;
      this.ready = null;
    }
    text(key, values = {}) {
      return Object.entries(values).reduce((s, [k,v]) => s.replaceAll(`{${k}}`, typeof v === 'number' ? this.number(v) : String(v)), this.labels[key] || key);
    }
    number(value) {
      this.numberFormatter ||= new Intl.NumberFormat(this.dataset.language || document.documentElement.lang || 'en', {maximumFractionDigits:1});
      return this.numberFormatter.format(value);
    }
    photo(value) {
      try {
        const url = new URL(value, new URL(this.dataset.source, location.href));
        return url.protocol === 'https:' && (url.hostname === location.hostname || url.hostname === 'cdn.shopify.com') ? url.href : null;
      } catch { return null; }
    }
    photos(row) { return (Array.isArray(row.photos) ? row.photos : []).map(value => this.photo(value)).filter(Boolean); }
    load() {
      if (this.ready) return this.ready;
      this.observer?.disconnect();
      const retry = this.querySelector('[data-rbnr-retry]');
      if (retry) retry.hidden = true;
      if (this.status) this.status.textContent = this.text('loading');
      this.ready = (async () => {
        try {
          if (this.mode === 'carousel') {
            const sources = JSON.parse(this.querySelector('[data-rbnr-sources]').textContent);
            const results = await Promise.allSettled(sources.map(async source => reviewRows(await reviewData(source.url),source.id)));
            this.allRows = results.flatMap(result => result.status === 'fulfilled' ? result.value : []);
            if (!this.allRows.length && results.every(result => result.status === 'rejected')) throw new Error('Review data unavailable');
          } else this.allRows = reviewRows(await reviewData(this.dataset.source),this.dataset.productId);
          const summary = reviewSummary(this.allRows);
          this.dataset.rating = summary.rating;
          this.dataset.count = summary.count;
          this.querySelectorAll('[data-rating-value]').forEach(node => {node.textContent = this.number(summary.rating);});
          this.querySelectorAll('[data-review-count]').forEach(node => {node.textContent = this.number(summary.count);});
          this.querySelectorAll('.rbnr-rating .rbnr-stars,.rbnr-score-card .rbnr-stars').forEach(node => {node.style.setProperty('--rbnr-fill',`${summary.rating * 20}%`);node.setAttribute('aria-label',`${this.number(summary.rating)} ${this.text('out_of')}`);});
          this.querySelector('.rbnr-rating')?.setAttribute('aria-label',`${this.text('rating_summary',{rating:summary.rating,count:summary.count,review_label:this.text(summary.count === 1 ? 'review' : 'reviews')})}. ${this.text('read_reviews')}`);
          this.rows = this.allRows;
          if (this.mode === 'carousel') {
            const selection = JSON.parse(this.querySelector('[data-rbnr-home-selection]')?.textContent || '[]');
            const byId = new Map(this.allRows.map(row => [row.id,row]));
            const selected = selection.filter(item => byId.has(item.id));
            if (selected.length) this.rows = selected.map(item => {
              const row = byId.get(item.id), photo = item.photo && this.photo(item.photo);
              return {...row,card_text:typeof item.text === 'string' ? item.text.trim() : '',photos:photo ? [photo,...row.photos] : row.photos};
            });
            else {
              const groups = [...new Set(this.allRows.map(row => row.product_id))].map(id => this.allRows.filter(row => row.product_id === id && this.photos(row).length));
              this.rows = [];
              for (let i = 0;this.rows.length < this.limit && groups.some(group => group[i]);i++) groups.forEach(group => {if (group[i] && this.rows.length < this.limit) this.rows.push(group[i]);});
            }
            const productIds = new Set(this.rows.map(row => row.product_id));
            const homeSummary = reviewSummary(this.allRows.filter(row => productIds.has(row.product_id)));
            this.querySelectorAll('[data-rating-value]').forEach(node => {node.textContent = this.number(homeSummary.rating);});
            this.querySelectorAll('[data-review-count]').forEach(node => {node.textContent = this.number(homeSummary.count);});
            const summaryStars = this.querySelector('.rbnr-home-summary .rbnr-stars');
            if (summaryStars) summaryStars.replaceWith(stars(homeSummary.rating,this.text('out_of')));
          }
          if (this.mode === 'feed') this.configureFeed(JSON.parse(this.querySelector('[data-rbnr-feed-selection]')?.textContent || '{}'));
          if (this.mode === 'snippets') {
            const selection = JSON.parse(this.querySelector('[data-rbnr-selection]').textContent);
            const byId = new Map(this.allRows.map(row => [row.id, row]));
            const selected = Array.isArray(selection.order) ? selection.order.filter(id => byId.has(id)) : [];
            const defaults = [...this.allRows].sort((a,b) => Number(this.photos(b).length > 0) - Number(this.photos(a).length > 0)).slice(0,10).map(row => row.id);
            const ids = [...new Set([selection.first, ...(selected.length ? selected : defaults)])].filter(id => byId.has(id)).slice(0,13);
            this.rows = ids.map(id => byId.get(id));
            this.hidden = !this.rows.length;
          }
          const toolbar = this.querySelector('[data-rbnr-toolbar]');
          if (toolbar) toolbar.hidden = !this.rows.length;
          if (this.mode === 'feed') this.renderOverview();
          this.render();
          return true;
        } catch {
          this.ready = null;
          if (this.status) this.status.textContent = this.text('failed');
          if (retry) retry.hidden = false;
          return false;
        }
      })();
      return this.ready;
    }
    move(delta) {
      if (this.mode === 'carousel') {
        const cards = [...this.list.children];
        if (!cards.length) return;
        const left = this.list.getBoundingClientRect().left;
        const positions = cards.map(card => card.getBoundingClientRect().left - left + this.list.scrollLeft);
        const current = positions.reduce((best, position, index) => Math.abs(position - this.list.scrollLeft) < Math.abs(positions[best] - this.list.scrollLeft) ? index : best, 0);
        const next = Math.max(0, Math.min(cards.length - 1, current + delta));
        this.list.scrollTo({left:positions[next],behavior:motion()});
        return;
      }
      this.slide = Math.max(0, Math.min(this.rows.length - 1, this.slide + delta));
      this.render();
    }
    configureFeed(selection = {}) {
      const byId = new Map(this.allRows.map(row => [row.id,row]));
      const first = [...new Set(Array.isArray(selection.top) ? selection.top : [])].filter(id => byId.has(id)).slice(0,10);
      const topIds = new Set(first);
      this.rows = [...first.map(id => byId.get(id)), ...this.allRows.filter(row => !topIds.has(row.id))].filter(row => row.id !== 'fbK_QWcumZ7');
      const lastPhotoReview = byId.get('fbK_QWcumZ7');
      if (lastPhotoReview) this.rows.push(lastPhotoReview);
    }
    renderOverview() {
      const distribution = this.querySelector('[data-rbnr-distribution]');
      if (distribution) {
        distribution.replaceChildren();
        for (let rating = 5; rating >= 1; rating--) {
          const count = this.allRows.filter(row => row.rating === rating).length;
          const button = el('button', 'rbnr-distribution-row');
          button.type = 'button';
          button.dataset.rating = rating;
          button.disabled = count === 0;
          button.setAttribute('aria-pressed', 'false');
          button.setAttribute('aria-label', this.text('rating_breakdown', {rating,count,review_label:this.text(count === 1 ? 'review' : 'reviews'),star_label:this.text(rating === 1 ? 'star' : 'stars')}));
          const label = el('span', 'rbnr-distribution-label', String(rating));
          label.append(el('span', 'rbnr-single-star', '★'));
          const track = el('span', 'rbnr-distribution-track');
          const fill = el('span', 'rbnr-distribution-fill');
          fill.style.width = `${this.allRows.length ? count / this.allRows.length * 100 : 0}%`;
          track.append(fill);
          button.append(label, track, el('span', 'rbnr-distribution-count', String(count)));
          button.addEventListener('click', () => {
            const filter = this.querySelector('[data-rbnr-filter]');
            filter.value = filter.value === String(rating) ? 'all' : String(rating);
            this.shown = this.limit;
            this.render();
            this.querySelector('[data-rbnr-progress]').setAttribute('aria-live', 'polite');
          });
          distribution.append(button);
        }
        distribution.hidden = !this.allRows.length;
      }
      const gallery = this.querySelector('[data-rbnr-gallery]');
      if (gallery) {
        gallery.replaceChildren();
        const seen = new Set();
        ([...this.allRows.filter(row => row.name !== 'Karina K.'), ...this.allRows.filter(row => row.name === 'Karina K.')]).forEach(row => this.photos(row).forEach(url => {
          if (seen.has(url)) return;
          seen.add(url);
          const button = el('button', 'rbnr-gallery-photo');
          button.type = 'button';
          button.setAttribute('aria-label', this.text('open_review', {name:row.name}));
          button.setAttribute('aria-haspopup', 'dialog');
          button.append(this.image(url));
          button.addEventListener('click', () => this.openReview(row, button));
          gallery.append(button);
        }));
        gallery.parentElement.hidden = !seen.size;
        gallery.classList.toggle('rbnr-gallery--compact', seen.size > 6);
        gallery.dataset.expanded = 'false';
        const toggle = this.querySelector('[data-rbnr-gallery-toggle]');
        if (toggle) {
          toggle.hidden = seen.size <= 6;
          toggle.setAttribute('aria-expanded', 'false');
          toggle.textContent = toggle.dataset.more;
        }
      }
    }
    cardId(id) { return `RBReview-${this.dataset.productId}-${id}`; }
    image(url, cls) {
      const img = el('img', cls);
      img.src = url;
      img.alt = this.text('photo');
      img.width = 600;
      img.height = 600;
      img.loading = 'lazy';
      img.addEventListener('error', () => { img.hidden = true; });
      return img;
    }
    openReview(row, opener) {
      if (Date.now() < (this.suppressClickUntil || 0)) return;
      this.opener = opener;
      const detail = this.querySelector('[data-rbnr-detail]');
      detail.replaceChildren(el('h3', 'rbnr-detail-name h2', row.name), stars(row.rating, this.text('out_of')), el('p', 'rbnr-copy', row.body));
      const photos = this.photos(row);
      if (photos.length) {
        const strip = el('div', 'rbnr-detail-photos');
        if (photos.length > 1) strip.tabIndex = 0;
        photos.forEach(url => strip.append(this.image(url)));
        detail.append(strip);
      }
      this.startDialogSession();
      try {
        this.dialog.showModal();
        this.dialog.scrollTop = 0;
        detail.scrollTop = 0;
        this.querySelector('[data-rbnr-close]').focus({preventScroll:true});
      } catch (error) {
        this.finishDialogSession();
        throw error;
      }
    }
    lockDialogBackground() {
      if (this.dialogScrollLock) return;
      const root = document.documentElement, body = document.body;
      const lock = {x:window.scrollX,y:window.scrollY,styles:[]};
      const set = (node, key, value) => {
        lock.styles.push({node,key,value:node.style.getPropertyValue(key),priority:node.style.getPropertyPriority(key),applied:value});
        node.style.setProperty(key, value, 'important');
      };
      set(root, 'scroll-behavior', 'auto');
      set(root, 'overflow', 'hidden');
      set(root, 'overscroll-behavior', 'none');
      set(body, 'position', 'fixed');
      set(body, 'top', `${-lock.y}px`);
      set(body, 'left', `${-lock.x}px`);
      set(body, 'width', '100%');
      set(body, 'overflow', 'hidden');
      set(body, 'overscroll-behavior', 'none');
      this.dialogScrollLock = lock;
    }
    unlockDialogBackground() {
      const lock = this.dialogScrollLock;
      if (!lock) return;
      this.dialogScrollLock = null;
      const restore = entry => {
        if (entry.node.style.getPropertyValue(entry.key) !== entry.applied || entry.node.style.getPropertyPriority(entry.key) !== 'important') return;
        if (entry.value) entry.node.style.setProperty(entry.key, entry.value, entry.priority);
        else entry.node.style.removeProperty(entry.key);
      };
      lock.styles.slice(1).reverse().forEach(restore);
      window.scrollTo({left:lock.x,top:lock.y,behavior:'instant'});
      restore(lock.styles[0]);
    }
    startDialogSession() {
      this.dialogViewportEvents?.abort();
      this.dialogViewportEvents = new AbortController();
      const signal = this.dialogViewportEvents.signal;
      const mobile = matchMedia('(max-width: 749px), (max-width: 1024px) and (max-height: 600px)');
      const update = () => {
        this.dialogViewportFrame = 0;
        this.dialog.classList.toggle('rbnr-dialog-mobile', mobile.matches);
        const copy = this.dialog.querySelector('.rbnr-copy');
        copy?.setAttribute('tabindex', '0');
        if (!mobile.matches) { this.unlockDialogBackground(); return; }
        this.lockDialogBackground();
        const viewport = window.visualViewport;
        const values = {top:viewport?.offsetTop || 0,left:viewport?.offsetLeft || 0,width:viewport?.width || window.innerWidth,height:viewport?.height || window.innerHeight};
        for (const [key,value] of Object.entries(values)) this.dialog.style.setProperty(`--rbnr-viewport-${key}`, `${value}px`);
      };
      const schedule = () => {
        if (!this.dialogViewportFrame) this.dialogViewportFrame = requestAnimationFrame(update);
      };
      window.addEventListener('resize', schedule, {signal});
      window.visualViewport?.addEventListener('resize', schedule, {signal});
      window.visualViewport?.addEventListener('scroll', schedule, {signal});
      mobile.addEventListener('change', schedule, {signal});
      update();
    }
    finishDialogSession(restoreFocus = true) {
      this.dialogViewportEvents?.abort();
      this.dialogViewportEvents = null;
      cancelAnimationFrame(this.dialogViewportFrame);
      this.dialogViewportFrame = 0;
      this.unlockDialogBackground();
      this.dialog?.classList.remove('rbnr-dialog-mobile');
      for (const key of ['top','left','width','height']) this.dialog?.style.removeProperty(`--rbnr-viewport-${key}`);
      if (restoreFocus && this.opener?.isConnected) this.opener.focus({preventScroll:true});
    }
    card(row) {
      const card = el('article', 'rbnr-card');
      card.dataset.reviewId = row.id;
      if (this.mode === 'feed') card.id = this.cardId(row.id);
      const button = el('button', 'rbnr-card-trigger');
      button.type = 'button';
      button.setAttribute('aria-label', this.text('open_review', {name:row.name}));
      button.setAttribute('aria-haspopup', 'dialog');
      const photos = this.photos(row);
      if (photos[0] && (this.mode !== 'snippets' || this.dataset.showPhotos !== 'false')) {
        const photo = el('span', 'rbnr-photo');
        photo.append(this.image(photos[0]));
        button.append(photo);
      }
      const body = el('span', 'rbnr-card-body');
      const name = el('span', 'rbnr-name', row.name), rating = stars(row.rating,this.text('out_of')), copy = el('span', 'rbnr-copy is-collapsed', this.mode === 'carousel' && row.card_text ? row.card_text : row.body);
      if (this.mode === 'carousel') body.append(rating,name,copy);
      else body.append(name,rating,copy);
      if (this.mode === 'carousel') body.append(el('span','rbnr-home-product',row.product_title));
      if (this.mode === 'feed' || this.mode === 'carousel') body.append(el('span', 'rbnr-card-more', this.text('read_review')));
      button.append(body);
      button.addEventListener('click', () => this.openReview(row, button));
      card.append(button);
      return card;
    }
    filteredRows() {
      let rows = this.rows.slice();
      const rating = this.querySelector('[data-rbnr-filter]')?.value;
      if (rating && rating !== 'all') rows = rows.filter(row => row.rating === Number(rating));
      if (this.querySelector('[data-rbnr-photos]')?.checked) rows = rows.filter(row => this.photos(row).length);
      const sort = this.querySelector('[data-rbnr-sort]')?.value;
      if (sort === 'highest') rows.sort((a,b) => b.rating - a.rating);
      if (sort === 'lowest') rows.sort((a,b) => a.rating - b.rating);
      return rows;
    }
    async revealReview(id) {
      if (this.mode !== 'feed' || !await this.load()) return false;
      const row = this.rows.find(item => item.id === id);
      if (!row) return false;
      const rating = this.querySelector('[data-rbnr-filter]');
      const photos = this.querySelector('[data-rbnr-photos]');
      let reset = false;
      if (rating.value !== 'all' && Number(rating.value) !== row.rating) { rating.value = 'all'; reset = true; }
      if (photos.checked && !this.photos(row).length) { photos.checked = false; reset = true; }
      const index = this.filteredRows().findIndex(item => item.id === id);
      this.shown = Math.max(this.limit, Math.ceil((index + 1) / this.limit) * this.limit);
      this.render();
      const card = document.getElementById(this.cardId(id));
      if (!card) return false;
      this.querySelectorAll('.rbnr-highlight').forEach(node => node.classList.remove('rbnr-highlight'));
      const header = document.querySelector('.shopify-section-header-sticky, sticky-header');
      const offset = Math.min(240, Math.max(24, (header?.getBoundingClientRect().height || 100) + 24));
      card.style.setProperty('--rbnr-scroll-offset', `${offset}px`);
      card.classList.add('rbnr-highlight');
      card.tabIndex = -1;
      card.scrollIntoView({behavior:motion(), block:'start'});
      card.focus({preventScroll:true});
      clearTimeout(this.highlightTimer);
      this.highlightTimer = setTimeout(() => card.classList.remove('rbnr-highlight'), 5000);
      this.status.textContent = [reset ? this.text('filters_reset') : '', this.text('review_selected', {name:row.name})].filter(Boolean).join(' ');
      history.replaceState(null, '', `#${encodeURIComponent(this.cardId(id))}`);
      return true;
    }
    render() {
      if (!this.list) return;
      if (this.mode === 'carousel') {
        this.list.replaceChildren(...this.rows.map(row => this.card(row)));
        this.status.textContent = this.rows.length ? '' : this.text('empty');
        this.querySelector('[data-rbnr-navigation]').hidden = this.rows.length < 2;
        return;
      }
      if (this.mode === 'snippets') {
        const row = this.rows[this.slide];
        this.list.replaceChildren(...(row ? [this.card(row)] : []));
        this.querySelector('[data-rbnr-navigation]').hidden = !row;
        this.querySelector('[data-rbnr-slide-count]').textContent = this.text('slide_count', {current:this.slide + 1,total:this.rows.length});
        this.querySelectorAll('[data-rbnr-prev]').forEach(button => { button.disabled = this.slide <= 0; });
        this.querySelectorAll('[data-rbnr-next]').forEach(button => { button.disabled = this.slide >= this.rows.length - 1; });
        this.querySelector('[data-rbnr-current-link]').href = row ? `#${encodeURIComponent(this.cardId(row.id))}` : `#RBReviews-${this.dataset.productId}`;
        this.status.textContent = '';
        return;
      }
      const rows = this.filteredRows();
      const currentRating = this.querySelector('[data-rbnr-filter]')?.value;
      this.querySelectorAll('[data-rbnr-distribution] button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.rating === currentRating)));
      this.list.replaceChildren(...rows.slice(0, this.shown).map(row => this.card(row)));
      this.status.textContent = rows.length ? '' : this.text(this.rows.length ? 'no_match' : 'empty');
      this.querySelector('[data-rbnr-progress]').textContent = rows.length ? this.text(rows.length === 1 ? 'shown_one' : 'shown', {shown:Math.min(rows.length,this.shown),total:rows.length}) : '';
      this.querySelector('[data-rbnr-more]').hidden = this.shown >= rows.length;
    }
  }
  customElements.define('rb-pillow-reviews', NativeReviews);
})();

